import { calculateArcPlcPayments } from "@datanac/datanac-api-toolkit/dist/arcplc/ArcPlcHelper";
import { COMMODITY_NAME_COTTON } from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { round_ex } from "@datanac/datanac-api-toolkit/dist/utility/Math";
import { ApiHelper } from "api/ApiHelper";
import { getUniqueBaseAcreCrops } from "components/Vault/Farm/helpers";
import _ from "lodash";

export const PROGRAM_NAME_ARCCO = "arc-co";
export const PROGRAM_NAME_PLC = "plc";

export const PRACTICE_NO_PRACTICE_SPECIFIED = "No Practice Specified";

export const MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED = "P";

/// Parameterized for single year context:
/// Multiple state/county/crops (via baseAcres)
export function ArcPlcService({ baseAcres }) {
  const _uniqueCropBaseAcres = baseAcres ? getUniqueBaseAcreCrops(baseAcres) : [];

  // --- --- ---

  const getPLCPaymentRates = async () => {
    const _plcPaymentRatesList = [];

    for (const _currentCrop of _uniqueCropBaseAcres) {
      const _fsaDataFilter = {
        crop_year: _currentCrop?.crop_year,
        commodity_name: _currentCrop?.commodity_name,
        type_name: _currentCrop?.type_name,
      };

      if (!_fsaDataFilter?.type_name) {
        delete _fsaDataFilter?.type_name;
      }

      var _plcPaymentRates = await ApiHelper.selectObject("FSA.ARCPLC", "PLCPaymentRates", _fsaDataFilter);
      // _currentCrop.PLCPaymentRates = _plcPaymentRates;

      if (_plcPaymentRates?.length) {
        _plcPaymentRates?.forEach((_plcPaymentRate) => {
          _plcPaymentRatesList.push(_plcPaymentRate);
        });
      }
    }

    return _plcPaymentRatesList;
  };

  // --- --- ---

  const getEffectiveReferencePrices = async () => {
    for (const _currentCrop of _uniqueCropBaseAcres) {
      const _fsaDataFilter = {
        crop_year: _currentCrop?.crop_year,
        commodity_name: _currentCrop?.commodity_name,
        type_name: _currentCrop?.type_name,
      };

      if (!_fsaDataFilter?.type_name) {
        delete _fsaDataFilter?.type_name;
      }

      var _effectiveReferencePrices = await ApiHelper.selectObject(
        "FSA.ARCPLC",
        "EffectiveReferencePrices",
        _fsaDataFilter
      );
      _currentCrop.EffectiveReferencePrices = _effectiveReferencePrices;
    }

    return _uniqueCropBaseAcres;
  };

  // --- --- ---

  const getMarketYearAveragePrices = async () => {
    for (const _currentCrop of _uniqueCropBaseAcres) {
      const _fsaDataFilter = {
        crop_year: _currentCrop?.crop_year,
        commodity_name: _currentCrop?.commodity_name,
        type_name: _currentCrop?.type_name,
      };

      if (!_fsaDataFilter?.type_name) {
        delete _fsaDataFilter?.type_name;
      }

      var _marketYearAveragePrices = await ApiHelper.selectObject(
        "FSA.ARCPLC",
        "MarketYearAveragePrices",
        _fsaDataFilter
      );
      _currentCrop.MarketYearAveragePrices = _marketYearAveragePrices;
    }

    return _uniqueCropBaseAcres;
  };

  // --- --- ---

  const getARCCOPrices = async () => {
    for (const _currentCrop of _uniqueCropBaseAcres) {
      const _fsaDataFilter = {
        crop_year: _currentCrop?.crop_year,
        commodity_name: _currentCrop?.commodity_name,
        type_name: _currentCrop?.type_name,
      };

      if (!_fsaDataFilter?.type_name) {
        delete _fsaDataFilter?.type_name;
      }

      var _arcCoPrices = await ApiHelper.selectObject("FSA.ARCPLC", "ARCCOPrices", _fsaDataFilter);
      _currentCrop.ARCCOPrices = _arcCoPrices;
    }

    return _uniqueCropBaseAcres;
  };

  // --- --- ---

  const getARCCOYields = async () => {
    const _arccoYieldsList = [];
    for (const _currentCrop of _uniqueCropBaseAcres) {
      const _fsaDataCountyFilter = {
        crop_year: _currentCrop?.crop_year,
        commodity_name: _currentCrop?.commodity_name,
        state_name: _currentCrop?.location_state_name,
        county_name: _currentCrop?.location_county_name,
        type_name: _currentCrop?.type_name,
      };

      if (!_fsaDataCountyFilter?.type_name) {
        delete _fsaDataCountyFilter?.type_name;
      }

      var _arccoYields = await ApiHelper.selectObject("FSA.ARCPLC", "ARCCOYields", _fsaDataCountyFilter);
      // _currentCrop.ARCCOYields = _arccoYields;

      if (_arccoYields?.length) {
        _arccoYields?.forEach((_arccoYield) => {
          _arccoYieldsList.push(_arccoYield);
        });
      }
    }

    return _arccoYieldsList;
  };

  // --- --- ---

  const getArcPlc = async ({
    plcPaymentRates,
    arccoYields,
    stressTestParameters /*Substitute for hypothetical*/,
    // actual_revenue,   effective_price
  }) => {
    const arcPlcParameters = {
      // PLCPaymentRates
      effective_reference_price: plcPaymentRates?.effective_reference_price || null,
      effective_price: plcPaymentRates?.effective_price || null, //substitute for hypothetical test
      national_loan_rate: plcPaymentRates?.national_loan_rate || null,

      // ARCCOYields:
      benchmark_price: arccoYields?.benchmark_price || null,
      benchmark_yield: arccoYields?.benchmark_yield || null,
      actual_revenue: stressTestParameters?.actual_revenue || 0, //substitute for hypothetical test
    };

    // const _programElections = await loadProgramElections();

    const _arcPlcResponses_async = [];
    for (var currentBaseAcre of _uniqueCropBaseAcres) {
      const _arcPlc = await calculateArcPlcPayments({ ...arcPlcParameters, ...currentBaseAcre });
      _arcPlcResponses_async.push({
        baseAcres: currentBaseAcre,
        arcPlcResponse: _arcPlc,
      });
    }

    const _arcPlcResponses = (await Promise.all(_arcPlcResponses_async)).filter((_ap) => _ap);

    const _totals = {
      arcPayment: 0,
      plcPayment: 0,
      base_acreage: 0,
      plc_yield: 0,
    };
    _arcPlcResponses.forEach((_currentArcPlc) => {
      if (_currentArcPlc?.arcPlcResponse?.arcco?.arcPayment)
        _totals.arcPayment += _currentArcPlc?.arcPlcResponse?.arcco?.arcPayment;
      if (_currentArcPlc?.arcPlcResponse?.plc?.plcPayment)
        _totals.plcPayment += _currentArcPlc?.arcPlcResponse?.plc?.plcPayment;
      if (_currentArcPlc?.baseAcres) _totals.base_acreage += _currentArcPlc.baseAcres.base_acreage;
      if (_currentArcPlc?.baseAcres)
        _totals.plc_yield += _currentArcPlc.baseAcres.plc_yield * _currentArcPlc.baseAcres.base_acreage;
    });
    _totals.plc_yield = _totals.plc_yield / _.sumBy(_arcPlcResponses, (arcplc) => arcplc.baseAcres.base_acreage);
    _totals.totalPayment = _totals.arcPayment + _totals.plcPayment;

    const _cropKeys = _.uniqBy(
      _arcPlcResponses.map((_arcplc) => ({
        commodity_name: _arcplc?.baseAcres?.commodity_name,
        type_name: _arcplc?.baseAcres?.type_name || null,
      })),
      (k) => `${k.commodity_name}||${k.type_name}`
    );
    const _crops = _cropKeys.map((key) => ({ ...key }));
    _crops.forEach((_currentCrop) => {
      const _cropArcPlcResponses = _arcPlcResponses.filter(
        (_arcPlc) =>
          _arcPlc?.baseAcres?.commodity_name === _currentCrop?.commodity_name &&
          (_arcPlc?.baseAcres?.type_name || null) === _currentCrop?.type_name
      );
      const _cropTotals = {
        arcPayment: 0,
        plcPayment: 0,
        base_acreage: 0,
        plc_yield: 0,
      };
      _cropArcPlcResponses.forEach((_currentArcPlc) => {
        if (_currentArcPlc?.arcPlcResponse?.arcco?.arcPayment)
          _cropTotals.arcPayment += _currentArcPlc?.arcPlcResponse?.arcco?.arcPayment;
        if (_currentArcPlc?.arcPlcResponse?.plc?.plcPayment)
          _cropTotals.plcPayment += _currentArcPlc?.arcPlcResponse?.plc?.plcPayment;
        if (_currentArcPlc?.baseAcres) _cropTotals.base_acreage += _currentArcPlc.baseAcres.base_acreage;
        if (_currentArcPlc?.baseAcres)
          _cropTotals.plc_yield += _currentArcPlc.baseAcres.plc_yield * _currentArcPlc.baseAcres.base_acreage;
      });
      _cropTotals.plc_yield =
        _cropTotals.plc_yield / _.sumBy(_cropArcPlcResponses, (arcplc) => arcplc.baseAcres.base_acreage);

      _currentCrop.arcPayment = _cropTotals.arcPayment;
      _currentCrop.plcPayment = _cropTotals.plcPayment;
      _currentCrop.base_acreage = _cropTotals.base_acreage;
      _currentCrop.plc_yield = _cropTotals.plc_yield;
    });

    // _programElections.forEach(_election => {
    //     const _arcPlcElections = _arcPlcResponses.filter(_arcPlc =>
    //         _arcPlc?.baseAcres?.commodity_name == _election?.commodity_name
    //         && _arcPlc?.baseAcres?.location_state_name == _election?.state_name
    //         && _arcPlc?.baseAcres?.location_county_name == _election?.county_name
    //     );

    //     _election.elections = _arcPlcElections;
    //     _election.totals = {
    //         arcPayment: 0,
    //         plcPayment: 0,
    //     };
    //     if (_arcPlcElections?.length) {
    //         if (_election.program_election == PROGRAM_NAME_ARCCO) {
    //             _election.totals.arcPayment = _.sumBy(_arcPlcElections, e => e.arcPlcResponse?.arcPayment);
    //         } else if (_election.program_election == PROGRAM_NAME_PLC) {
    //             _election.totals.plcPayment = _.sumBy(_arcPlcElections, e => e.arcPlcResponse?.plcPayment);
    //         }
    //     }
    // })

    // _totals.programElections = {
    //     arcPayment: _.sumBy(_programElections, pe => pe.totals?.arcPayment),
    //     plcPayment: _.sumBy(_programElections, pe => pe.totals?.plcPayment),
    // }
    // _totals.programElections.totalPayment = _totals.programElections.arcPayment + _totals.programElections.plcPayment;

    return {
      arcPlcResponses: _crops,
      // programElections: _programElections,
      totals: _totals,

      arcPlcParameters,
    };
  };

  // --- --- ---

  // Helper function to create dummy PLC budget county practice
  const createDummyPlcBudgetCountyPractice = ({
    crop_year,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name,
    marketYearAveragePrices,
  }) => {
    return {
      year: crop_year,
      state_name: location_state_name,
      county_name: location_county_name,
      commodity_name: commodity_name,
      practice_name: "Non-Irrigated",
      type_name:
        type_name ||
        (commodity_name?.toLowerCase() === "corn"
          ? "Grain"
          : commodity_name?.toLowerCase() === "wheat"
            ? "Winter"
            : "No Type Specified"),
      projected_price: marketYearAveragePrices?.price || 4.0,
      reported_acreage: 0,
      reinsurance_year: crop_year,
    };
  };

  // Helper function to create stress test parameters
  // Specially for ARC/PLC.
  const createStressTestParameters = ({ commodity_name, benchmarkYield, marketYearAveragePrices, plcYield = null }) => {
    const COTTONSEED_TURNOUT_WEIGHT_FACTOR = 2.5;

    if (commodity_name === COMMODITY_NAME_COTTON) {
      return {
        county_yield_low: round_ex((plcYield * 0.8) / COTTONSEED_TURNOUT_WEIGHT_FACTOR, 2),
        county_yield_high: round_ex((plcYield * 1.1) / COTTONSEED_TURNOUT_WEIGHT_FACTOR, 2),
        yield_low: round_ex((plcYield * 0.8) / COTTONSEED_TURNOUT_WEIGHT_FACTOR, 2),
        yield_high: round_ex((plcYield * 1.1) / COTTONSEED_TURNOUT_WEIGHT_FACTOR, 2),
        price_low: round_ex(marketYearAveragePrices?.price * 0.8, 4),
        price_high: round_ex(marketYearAveragePrices?.price * 1.1, 4),
      };
    } else {
      return {
        county_yield_low: round_ex(benchmarkYield * 0.8, 2),
        county_yield_high: round_ex(benchmarkYield * 1.1, 2),
        yield_low: round_ex(benchmarkYield * 0.8, 2),
        yield_high: round_ex(benchmarkYield * 1.1, 2),
        price_low: round_ex(marketYearAveragePrices?.price * 0.8, 4),
        price_high: round_ex(marketYearAveragePrices?.price * 1.1, 4),
      };
    }
  };

  const actions = {
    getArcPlc,

    getPLCPaymentRates,

    getARCCOPrices,
    getARCCOYields,

    getEffectiveReferencePrices,

    getMarketYearAveragePrices,

    // Stress test helpers
    createDummyPlcBudgetCountyPractice,
    createStressTestParameters,
  };

  return {
    actions,
  };
}
